/**
 * Mission Status Card
 * 
 * WHY: This is the primary "at-a-glance" widget for the SoftwareIQ dashboard.
 * Analysts need to immediately see the severity distribution of findings
 * to prioritize their vetting and remediation work.
 * 
 * @see .docs/architecture/phase5_security_assessment.md - Phase 6 Requirements
 */

import { useQuery } from '@tanstack/react-query'
import { api, type Finding } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Shield, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MissionStatusCardProps {
  missionId: string
  className?: string
}

interface SeverityCounts {
  critical: number
  high: number
  medium: number
  low: number
  total: number
}

/**
 * Aggregates finding counts by severity level.
 * WHY: Findings have jfac_risk.label for severity, not a direct severity property.
 */
function aggregateSeverityCounts(findings: Finding[]): SeverityCounts {
  const counts: SeverityCounts = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    total: findings.length,
  }

  for (const finding of findings) {
    // Use jfac_risk.label for severity classification
    const severity = finding.jfac_risk?.label?.toLowerCase() || 'low'
    if (severity === 'critical') counts.critical++
    else if (severity === 'high') counts.high++
    else if (severity === 'medium') counts.medium++
    else counts.low++
  }

  return counts
}

/**
 * Individual severity stat display.
 */
function SeverityStat({
  label,
  count,
  variant,
  icon: Icon,
}: {
  label: string
  count: number
  variant: 'critical' | 'high' | 'medium' | 'low'
  icon: React.ElementType
}) {
  const colorClasses = {
    critical: 'text-red-500 bg-red-500/10 border-red-500/20',
    high: 'text-orange-500 bg-orange-500/10 border-orange-500/20',
    medium: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20',
    low: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-4 rounded-lg border',
        colorClasses[variant]
      )}
    >
      <Icon className="h-5 w-5 mb-2" />
      <span className="text-2xl font-bold tabular-nums">{count}</span>
      <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
    </div>
  )
}

export function MissionStatusCard({ missionId, className }: MissionStatusCardProps) {
  const { data: findings, isLoading, error } = useQuery({
    queryKey: ['softwareiq', 'findings', missionId],
    queryFn: () => api.softwareiq.findings.list(missionId),
    enabled: !!missionId,
    staleTime: 30_000, // 30 seconds - dashboard data can be slightly stale
  })

  const counts = findings ? aggregateSeverityCounts(findings) : null

  if (error) {
    return (
      <Card className={cn('border-destructive/50', className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Error Loading Findings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Failed to load mission status'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Mission Status
            </CardTitle>
            <CardDescription>Finding severity distribution</CardDescription>
          </div>
          {counts && (
            <Badge variant="outline" className="text-lg px-3 py-1">
              {counts.total} Total
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        ) : counts ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SeverityStat
              label="Critical"
              count={counts.critical}
              variant="critical"
              icon={AlertTriangle}
            />
            <SeverityStat
              label="High"
              count={counts.high}
              variant="high"
              icon={AlertCircle}
            />
            <SeverityStat
              label="Medium"
              count={counts.medium}
              variant="medium"
              icon={Info}
            />
            <SeverityStat
              label="Low"
              count={counts.low}
              variant="low"
              icon={CheckCircle}
            />
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No findings yet</p>
            <p className="text-xs mt-1">Run a scan to discover vulnerabilities</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
